import type { ComponentProps, ReactNode } from "react";
import { Slot } from "radix-ui";
import { AltArrowDown as ChevronDown } from "@papier/ui/components/icons";
import { Button } from "@papier/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@papier/ui/components/ui/dropdown-menu";
import { cn } from "@papier/ui/lib/utils";

export function WorkspaceLayout({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("flex min-h-full shrink-0 flex-col bg-muted/25 lg:flex-row", className)}
      {...props}
    />
  );
}

export function WorkspaceScrollArea({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="workspace-scroll-area"
      className={cn(
        "relative flex min-w-0 flex-1 flex-col overflow-y-auto bg-background [scrollbar-gutter:stable]",
        className,
      )}
      {...props}
    />
  );
}

export function WorkspaceContent({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div tabIndex={-1} className={cn("min-w-0 flex-1 outline-none", className)} {...props}>
      <div
        data-slot="workspace-content-frame"
        className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-7 lg:px-9 lg:py-9 xl:px-12"
      >
        {children}
      </div>
    </div>
  );
}

export function WorkspaceSidebar({
  header,
  children,
  mobileNavigation,
  footer,
  className,
  ...props
}: ComponentProps<"aside"> & {
  header: ReactNode;
  mobileNavigation: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <aside
      data-slot="workspace-sidebar"
      className={cn(
        "shrink-0 border-b border-border/60 bg-background lg:sticky lg:top-0 lg:flex lg:h-[calc(100svh-4rem-1px)] lg:w-60 lg:flex-col lg:self-start lg:overflow-hidden lg:border-b-0 xl:w-64",
        className,
      )}
      {...props}
    >
      {header}
      {children}
      <div className="lg:hidden">{mobileNavigation}</div>
      {footer && (
        <div className="mt-auto hidden shrink-0 space-y-4 p-5 pt-8 lg:block">{footer}</div>
      )}
    </aside>
  );
}

export function WorkspaceSidebarHeader({
  backLink,
  avatar,
  label,
  name,
}: {
  backLink: ReactNode;
  avatar: ReactNode;
  label: string;
  name: string;
}) {
  return (
    <div data-slot="workspace-sidebar-header" className="shrink-0 px-4 pt-4 pb-3 lg:pt-6 lg:pb-6">
      <Slot.Root className="mb-4 hidden items-center gap-2 rounded px-2 text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary lg:inline-flex [&_svg]:size-3.5">
        {backLink}
      </Slot.Root>
      <div data-slot="workspace-sidebar-identity" className="flex min-w-0 items-center gap-3 px-2">
        <Slot.Root className="size-9 shrink-0">{avatar}</Slot.Root>
        <div className="min-w-0">
          <p className="text-[10px] text-muted-foreground">{label}</p>
          <p className="truncate text-sm font-semibold" title={name}>
            {name}
          </p>
        </div>
      </div>
    </div>
  );
}

export function WorkspaceSidebarNav({ className, ...props }: ComponentProps<"nav">) {
  return (
    <nav
      data-slot="workspace-sidebar-nav"
      className={cn(
        "hidden space-y-6 px-3 pb-6 lg:block lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain [scrollbar-gutter:stable]",
        className,
      )}
      {...props}
    />
  );
}

export function WorkspaceSidebarGroup({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <div>
      {label && <p className="mb-2 px-3 text-[11px] font-medium text-muted-foreground">{label}</p>}
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function WorkspaceSidebarLink({
  asChild = false,
  className,
  ...props
}: ComponentProps<"a"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "a";
  return (
    <Comp
      data-slot="workspace-sidebar-link"
      className={cn(
        "relative flex min-w-0 items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground outline-none transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none [&>svg]:size-[18px] [&>svg]:shrink-0 [&>svg]:stroke-[1.7]",
        "aria-[current=page]:bg-linear-to-r aria-[current=page]:from-primary/10 aria-[current=page]:to-transparent aria-[current=page]:font-medium aria-[current=page]:text-primary aria-[current=page]:hover:bg-transparent aria-[current=page]:before:absolute aria-[current=page]:before:inset-y-3 aria-[current=page]:before:left-0 aria-[current=page]:before:w-0.5 aria-[current=page]:before:rounded-full aria-[current=page]:before:bg-primary",
        "aria-[current=location]:bg-linear-to-r aria-[current=location]:from-primary/10 aria-[current=location]:to-transparent aria-[current=location]:font-medium aria-[current=location]:text-primary aria-[current=location]:hover:bg-transparent",
        className,
      )}
      {...props}
    />
  );
}

export function WorkspaceSidebarPageMenu({
  label,
  icon,
  ariaLabel,
  children,
}: {
  label: string;
  icon: ReactNode;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <div className="px-4 pb-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            aria-label={ariaLabel}
            className="w-full min-w-0 justify-between rounded-lg bg-background shadow-none"
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              <Slot.Root className="size-4 shrink-0">{icon}</Slot.Root>
              <span className="truncate">{label}</span>
            </span>
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[calc(100vw-2rem)] max-w-sm" align="start">
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
